import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, RefreshCw, ChevronDown } from 'lucide-react';
import { FaYoutube, FaTwitch, FaFacebook, FaTwitter, FaUser } from 'react-icons/fa';
import type { CurrentUser } from '@/store/slices/envsSlice';
import { getLogoutUrl } from '@/lib/utils/authUrls';

/**
 * 원본: .old-gc-mono/apps/gc-zoku/components/UserMenu.js
 * Avatar + 채널 아이콘 + 이름 + 드롭다운(Switch channel, Sign out)
 */

/** 배포 환경에서는 signout 등이 백엔드로 직접 전달되어야 함 */
const HAS_API_BASE = !!import.meta.env.VITE_API_BASE_URL;

interface UserMenuProps {
  user: CurrentUser | null;
}

const CHANNEL_ICONS: Record<string, React.ReactNode> = {
  youtube: <FaYoutube />,
  twitch: <FaTwitch />,
  facebook: <FaFacebook />,
  twitter: <FaTwitter />,
};

function UserMenu({ user }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSwitchDialog, setShowSwitchDialog] = useState(false);
  const [channelId, setChannelId] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitch = useCallback(() => {
    if (channelId.trim()) {
      window.location.href = `/dev/switch_channel/${encodeURIComponent(channelId.trim())}`;
    }
  }, [channelId]);

  const handleSignOut = useCallback((e: React.MouseEvent) => {
    if (!HAS_API_BASE) return; // 로컬: 기본 <a> 동작 (Vite proxy)
    e.preventDefault();
    // 백엔드 logout을 fetch로 호출하여 세션 쿠키만 제거하고, 현재 도메인의 로그인 페이지로 이동
    fetch(getLogoutUrl({ direct: true }), { credentials: 'include', redirect: 'manual' })
      .finally(() => { window.location.href = '/login'; });
  }, []);

  if (!user) return null;

  const channel = (user.channels?.[0] ?? {}) as Record<string, unknown>;
  const channelType = (channel.type as string) || (channel.channelType as string) || '';
  const thumbnail = (user as Record<string, unknown>).thumbnail as string
    || channel.thumbnail as string
    || '/assets/favicon/favicon-32x32.png';
  const title = (user as Record<string, unknown>).title as string
    || user.firstName
    || user.email
    || 'User';
  const channelIcon = CHANNEL_ICONS[channelType] || <FaUser />;
  const isDev = import.meta.env.DEV;

  return (
    <>
      <div className="um-root" ref={dropdownRef}>
        <button className="um-trigger" onClick={() => setIsOpen(!isOpen)}>
          <img src={thumbnail} alt="" className="um-avatar" />
          <span className="um-name">
            <span className="um-channel-icon">{channelIcon}</span>
            {title}
          </span>
          <ChevronDown size={12} className={`um-chevron ${isOpen ? 'um-chevron-up' : ''}`} />
        </button>

        {isOpen && (
          <div className="um-dropdown">
            <Link to="/bridge/my_campaigns" className="um-item um-mobile-only" onClick={() => setIsOpen(false)}>
              Dashboard
            </Link>
            <Link to="/bridge/campaigns" className="um-item um-mobile-only" onClick={() => setIsOpen(false)}>
              Campaigns
            </Link>
            <Link to="/bridge/profile" className="um-item um-mobile-only" onClick={() => setIsOpen(false)}>
              Profile
            </Link>
            {isDev && (
              <button
                className="um-item"
                onClick={(e) => {
                  e.preventDefault();
                  setShowSwitchDialog(true);
                  setIsOpen(false);
                }}
              >
                <RefreshCw size={13} /> Switch channel (test)
              </button>
            )}
            <a href={getLogoutUrl({ direct: HAS_API_BASE })} className="um-item" onClick={handleSignOut}>
              <LogOut size={13} /> Sign out
            </a>
          </div>
        )}
      </div>

      {/* Switch channel 모달 (개발 환경 전용) */}
      {showSwitchDialog && (
        <div className="um-modal-overlay" onClick={() => setShowSwitchDialog(false)}>
          <div className="um-modal" onClick={(e) => e.stopPropagation()}>
            <p className="um-modal-title">Switch Channel</p>
            <input
              className="um-modal-input"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              placeholder='Enter channel ID or just "me"'
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleSwitch()}
            />
            <div className="um-modal-actions">
              <button className="um-modal-cancel" onClick={() => setShowSwitchDialog(false)}>Cancel</button>
              <button className="um-modal-ok" onClick={handleSwitch}>Switch</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserMenu;
